import { AccountPayableModel } from 'api-hooks/account-payable';
import { AccountReceivableModel } from 'api-hooks/account-receivable';
import { ApprovalItemModel } from 'api-hooks/approval-item';
import { ApprovalableTypeEnum } from 'api-hooks/common';
import { ActivityPurchaseModel } from 'api-hooks/order/activity';
import { CruisePurchaseModel } from 'api-hooks/order/cruise';
import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { FlightPurchaseModel } from 'api-hooks/order/ticket-order';
import { DocumentPurchaseModel } from 'api-hooks/order/travel-document';
import { OrderAdjustmentModel } from 'api-hooks/order-adjustment';
import { OtherExpensesModel } from 'api-hooks/other-expense';
import { OtherIncomeModel } from 'api-hooks/other-income';
import { ProjectModel } from 'api-hooks/projects';
import { ReceiptModel } from 'api-hooks/receipt';
import { RefundModel } from 'api-hooks/refund/refund';
import { RefundReleaseAgentModel } from 'api-hooks/refund/refund-release-agent';
import { RefundReleaseUserModel } from 'api-hooks/refund/refund-release-user';
import { ActivitySaleModel } from 'api-hooks/sale/activity';
import { CruiseSaleModel } from 'api-hooks/sale/cruise';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TicketSaleModel } from 'api-hooks/sale/ticket-sale';
import { TravelDocumentSalesModel } from 'api-hooks/sale/travel-document';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { TourSalesModel } from 'api-hooks/tour/tour-sale';
import { BalanceAdjustmentModel } from 'api-hooks/transactions/balance-adjustment';
import { BankTransactionModel } from 'api-hooks/transactions/bank-transaction';
import { CashTransactionModel } from 'api-hooks/transactions/cash-transaction';
import { IncomeCostModel } from 'api-hooks/transactions/income-cost';
import { TransferModel } from 'api-hooks/transactions/transfer';
import { UploadFileTypeEnum } from 'api-hooks/upload/upload.model';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import { HalfContainer, SectionContainer } from 'components/styles';
import FormHeader from 'components/widgets/form-header';
// import useDialog from 'hooks/use-dialog';
// import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ActionComponent from './action-component';
import ApprovalVerdictTable from './approval-verdict-table';
import { ApprovalItemFormType } from './form-type';
import DetailSection from './section/detail-section';
import defaultValueProject from './section/project/default-value';
import defaultValuesPurchase from './section/purchase/default-values';
import defaultValuesRefund from './section/refund/default-values';
import defaultValuesSale from './section/sale/default-values';
import defaultValue from './section/tipping/default-value';
import defaultValuesTour from './section/tour/default-values';
import defaultValuesTransaction from './section/transaction/default-values';

interface Props {
  approval: ApprovalItemModel;
  customViewName?: string | undefined;
}

export default function ApprovalItemForm(props: Props) {
  const { t } = useTranslation();
  const { approval, customViewName } = props;
  // const { close } = useNavigation();
  // const dialog = useDialog();
  const defaultValues = React.useMemo<any>(() => {
    let approvalable: any = {};
    switch (approval.approvalableType) {
      case ApprovalableTypeEnum.Sale:
        approvalable = defaultValuesSale(
          approval.approvalable as
            | TicketSaleModel
            | HotelSaleModel
            | CruiseSaleModel
            | TravelDocumentSalesModel
            | TourSalesModel
            | ActivitySaleModel,
          approval.approvalable.transactionType,
          approval.workflow,
        );
        break;
      case ApprovalableTypeEnum.Purchase:
        approvalable = defaultValuesPurchase(
          approval.approvalable as
            | FlightPurchaseModel
            | HotelPurchaseModel
            | CruisePurchaseModel
            | DocumentPurchaseModel
            | ActivityPurchaseModel,
          approval.approvalable.transactionType,
        );
        break;
      case ApprovalableTypeEnum.AccountPayable:
      case ApprovalableTypeEnum.AccountReceivable:
      case ApprovalableTypeEnum.BankTransaction:
      case ApprovalableTypeEnum.CashTransaction:
      case ApprovalableTypeEnum.BalanceAdjustment:
      case ApprovalableTypeEnum.Transfer:
      case ApprovalableTypeEnum.IncomeAndCost:
      case ApprovalableTypeEnum.OrderAdjustment:
      case ApprovalableTypeEnum.Receipt:
        approvalable = defaultValuesTransaction(
          approval.approvalable as
            | AccountPayableModel
            | AccountReceivableModel
            | IncomeCostModel
            | BalanceAdjustmentModel
            | BankTransactionModel
            | CashTransactionModel
            | TransferModel
            | ReceiptModel
            | OrderAdjustmentModel,
          approval.approvalableType,
          t,
        );
        break;
      case ApprovalableTypeEnum.Project:
        approvalable = defaultValueProject(
          approval.approvalable as ProjectModel,
        );
        break;
      case ApprovalableTypeEnum.Tour:
        approvalable = defaultValuesTour(approval.approvalable as TourModel);
        break;

      case ApprovalableTypeEnum.OtherExpenses:
      case ApprovalableTypeEnum.OtherIncome:
        approvalable = defaultValue(
          approval.approvalable as OtherExpensesModel | OtherIncomeModel,
          approval.approvalableType,
        );
        break;
      case ApprovalableTypeEnum.Refund:
      case ApprovalableTypeEnum.ReleaseSale:
      case ApprovalableTypeEnum.ReleasePurchase:
        approvalable = defaultValuesRefund(
          approval.approvalable as
            | RefundModel
            | RefundReleaseUserModel
            | RefundReleaseAgentModel,
          approval.approvalableType,
          t,
        );
        break;
    }
    return {
      requestedBy: approval.employee.name,
      reason: approval.reason,
      approvalableType: approval.approvalableType,
      files: approval.files.map((file) => ({
        id: file.id,
        file: file.name,
        url: file.url,
      })),
      approvalable,
    };
  }, [approval, t]);

  const methods = useForm<ApprovalItemFormType>({
    defaultValues,
  });

  const onSubmit = () => {};
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={false}
      customViewName={customViewName}
    >
      <FormHeader
        data={approval}
        title={
          approval
            ? customViewName ?? t('common:approval')
            : t('common:add_extra', { extra: t('common:approval') })
        }
        noEdit
        noDelete
        onRenderComponent={<ActionComponent approval={approval} />}
      />
      <div>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="requestedBy"
              label={t('common:request')}
              placeholder={t('common:request')}
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="reason"
              label={t('common:reason')}
              placeholder={t('common:reason')}
            />
          </HalfContainer>
        </SectionContainer>
        <HalfContainer>
          <Input
            type="files"
            name="files"
            label={t('common:proof_of_change')}
            valueKey="file"
            previewKey="url"
            fileType={UploadFileTypeEnum.ApprovalItem}
          />
        </HalfContainer>
        <ApprovalVerdictTable approvalVerdicts={approval.approvalVerdicts} />
        <Separator gap={32} />
        <DetailSection />
        <Separator gap={24} />
      </div>
    </Form>
  );
}
